import React from 'react';
import PropTypes from 'prop-types';
import { Popover } from 'antd';

import { STAGE_FILTERS, STAGE_OPTIONS } from 'constants/stage.const';
import { isString, isArray, isFunction, isObject, isEmpty, isNotEmpty } from 'util/utils';
import { classNames } from 'util/utils';
import { useBoolean, useCustomRef } from 'util/hooks';

import statusSvg from 'assets/svg/status.svg';
import 'styles/StagePopover.less';

const StageOption = React.memo((props) => {
  const { stage = {}, stageKey, onChange, value, displayCode } = props;

  const stageValue = React.useMemo(() => STAGE_FILTERS?.[stageKey] ?? {}, [stageKey]);

  const isSelected = React.useMemo(() => {
    return value?.includes(stageKey) || (stageKey === 'ALL' && isEmpty(value));
  }, [stageKey, value]);

  const stageCode = React.useMemo(() => {
    if (!displayCode) return '';
    return `${Object.values(stageValue)
      .map((id) => {
        if (isString(id)) return id;
        if (isArray(id)) return id.join('');
        if (isObject(id) && isArray(id?.in)) return id.in.join('');
        return undefined;
      })
      .filter(Boolean)
      .join('')} - `;
  }, [displayCode, stageValue]);

  const handleClick = React.useCallback(
    (event) => {
      if (isFunction(event?.preventDefault)) event.preventDefault();
      if (isFunction(onChange)) onChange(stageKey);
    },
    [onChange, stageKey],
  );

  return (
    <a
      className={classNames(['StagePopoverStage', isSelected && 'selected'])}
      onClick={handleClick}
      style={{ minWidth: '200px' }}
    >
      {stage?.color && <div style={{ backgroundColor: stage?.color }} className="SelectcolorWiseList" />}
      <span>{[stageCode, stage?.label].filter(Boolean).join()}</span>
    </a>
  );
});
StageOption.displayName = 'StageOption';

const StagePopoverContent = React.memo((props) => {
  const { onChange, applyFilter, clearFilter, value, displayCode } = props;
  const options = STAGE_OPTIONS?.CUSTOMER;

  return (
    <>
      <div className="d-flex flex-wrap filterDropdown StagePopover">
        {Object.entries(options).map(([key, stage]) => (
          <StageOption
            key={key}
            stage={stage}
            stageKey={key}
            onChange={onChange}
            value={value}
            displayCode={displayCode}
          />
        ))}
        <div className="FilterTopButtonContainer width-full d-flex flex-wrap">
          <div className="FilterTopButton" role="button" onClick={clearFilter}>
            Clear
          </div>
          <div className="FilterTopButton mr-10" role="button" onClick={applyFilter}>
            Apply
          </div>
        </div>
      </div>
    </>
  );
});
StagePopoverContent.displayName = 'StagePopoverContent';

export const getStagePopoverContent = (props) => <StagePopoverContent {...props} />;

const StagePopover = React.memo((props) => {
  const { onChange, initialValue = [] } = props;
  const { displayCode } = props;

  const [keyList, setKeyList] = React.useState(isArray(initialValue) ? initialValue : []);
  const [visible, setVisible] = useBoolean();
  const _keyListRef = useCustomRef(keyList);
  const _onChangeRef = useCustomRef(false);

  React.useEffect(() => void _onChangeRef.set(onChange), [_onChangeRef, onChange]);

  const handleChange = React.useCallback(
    (key) => {
      setKeyList((list) => {
        _keyListRef.set([...list]);
        if (isEmpty(key) || key === 'ALL') return [];
        list.includes(key) ? list.splice(list.indexOf(key), 1) : list.push(key);
        return [...list];
      });
    },
    [_keyListRef],
  );

  const filter = React.useMemo(() => {
    const filterList = keyList.map((key) => STAGE_FILTERS?.[key]).filter(isNotEmpty);
    return isEmpty(filterList) ? undefined : filterList;
  }, [keyList]);

  const applyFilter = React.useCallback(() => {
    const _onChange = _onChangeRef.get();
    if (isFunction(_onChange)) _onChange(filter, keyList);
    setVisible.false();
  }, [_onChangeRef, filter, keyList, setVisible]);

  const clearFilter = React.useCallback(() => {
    const _onChange = _onChangeRef.get();
    if (isFunction(_onChange)) _onChange(undefined, []);
    setVisible.false();
    setKeyList([]);
  }, [_onChangeRef, setVisible]);

  const popoverContent = React.useMemo(() => {
    return getStagePopoverContent({
      applyFilter,
      clearFilter,
      onChange: handleChange,
      value: keyList,
      displayCode,
    });
  }, [applyFilter, clearFilter, displayCode, handleChange, keyList]);

  return (
    <Popover
      content={popoverContent}
      visible={visible}
      placement="bottomRight"
      overlayStyle={{ width: '645px' }}
      getPopupContainer={(trigger) => trigger?.parentNode}
    >
      <div
        title="View/Change Status"
        role="button"
        className="StagePopoverContainer pointer"
        onClick={visible ? setVisible.false : setVisible.true}
      >
        <img style={{ width: '18px', height: '18px', margin: '0 -4px 6px 10px' }} src={statusSvg} alt="Status" />
      </div>
    </Popover>
  );
});

StagePopover.displayName = 'StagePopover';
StagePopover.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
};

export default StagePopover;
